/**
 * Générateur — Il calcule et interprète la médiane d'une série de données de
 * petit effectif total.
 * Attendu ID : 165 | Niveau : 4ème | Domaine : Données/Statistiques (descripteur 14)
 *
 * Source : doc prof OGD/FONCTIONS 4e, objectif O2 (Ex.2-6 : notes, âges, marais
 * salant, salaires, PM10). Le doc insiste sur : ranger la série (geste clé),
 * effectif impair (valeur centrale) vs pair (demi-somme), interprétation, et le
 * débat médiane / moyenne (robustesse aux valeurs extrêmes).
 *
 * Cartes (état d'origine, juillet 2026) : « Ranger, puis médiane (effectif
 * impair) » (ordonner, saisie, QCM d'interprétation), « Médiane d'un effectif
 * pair » (saisie, trous, QCM de méthode), « Médiane depuis un tableau
 * d'effectifs » (saisie, trous), « Médiane ou moyenne ? » (saisie, QCM, V/F).
 *
 * Revue 2026-09-23 (vague 3), décisions du coordinateur appliquées en mode
 * auto. Attendu de fin de 4e (Eduscol, attendus de fin d'année, p. 5) : « Il
 * calcule et interprète la médiane d'une série de données de petit effectif
 * total. » ; « Il détermine et interprète la médiane de séries dont l'effectif
 * total (pair ou impair) est inférieur ou égal à 30, présentées sous forme de
 * données brutes, d'un tableau ou d'un diagramme en bâtons. » Cours (19Statist,
 * partie 2) : « La médiane partage une série ordonnée en deux groupes de même
 * effectif », « il faut ordonner les séries », « Médiane = (12 + 13) : 2 =
 * 12,5 », séries à valeurs répétées (Margot 12 ; 12 ; 17 ; 17), tableau
 * (« L'effectif total est de 8 + 6 + 7 + 4 = 25. La médiane est donc la 13e
 * valeur », « au moins 13 élèves … supérieure ou égale à 60 minutes »),
 * diagramme en bâtons (27 notes, « 14e valeur », un effectif nul). Absents du
 * cours : « demi-somme », « valeur centrale », « étendue ».
 *   - Rappel brouillon IMPÉRATIF sur toutes les cartes (RAPPEL_BROUILLON),
 *     juste avant la question : ordonner sept à douze valeurs, cumuler des
 *     effectifs, (a + b) : 2 se font à la main.
 *   - impair_ordonner SORTIE (ranger seul, geste de cycle 3, la médiane n'y
 *     apparaît pas).
 *   - Carte « Médiane ou moyenne ? » SORTIE, mode auto, À ARBITRER
 *     (moyenne_texte, moyenne_qcm_unique, moyenne_tableau_vf) : calculer une
 *     moyenne est une ligne de 5e et l'automatisme 1125 ; « étendue », « valeur
 *     extrême » et la robustesse ne sont ni dans l'attendu ni dans le cours.
 *   - Séries brutes : valeurs RÉPÉTÉES admises (tirage avec remise, comme les
 *     séries du cours) ; le nombre de valeurs n'est plus écrit dans l'énoncé
 *     (le compter fait partie du geste) ; contextes précisés (des colis pesés,
 *     des plants mesurés, la température à midi pendant plusieurs jours, les
 *     derniers matchs d'une équipe, les trajets de quelques élèves) ; nouveau
 *     tirage tant que la valeur écrite au milieu du relevé non ordonné (ou la
 *     moyenne des deux, effectif pair) donne la médiane (13,1 % avant) : ne pas
 *     ordonner n'est plus jamais récompensé.
 *   - impair_texte : « (Pense à ranger la série d'abord.) » retiré ; 7, 9 ou 11
 *     valeurs.
 *   - impair_qcm_unique : quatre phrases sur la médiane donnée m, toutes avec
 *     m, jamais la clé la plus longue ; clé dans les mots du cours (« Au moins
 *     la moitié des colis pèsent m kg ou plus », ou « ou moins ») ; distracteurs
 *     milieu_sans_ranger (DEPP 2de 2025 q.14, la principale), moyenne_pour_
 *     mediane, milieu_des_extremes, chacun faux par construction (la valeur du
 *     milieu du relevé différente de m ; moyenne et milieu des extrêmes à au
 *     moins 1 de m). Le distracteur « la valeur qui revient le plus souvent »,
 *     absurde sur une série sans répétition, sort.
 *   - pair_texte : « L'effectif est PAIR. » retiré ; 6, 8, 10 ou 12 valeurs.
 *   - pair_texte_trou_saisie : trous ANCRÉS, une ligne chacun, « La 4ᵉ valeur
 *     est … », « La 5ᵉ valeur est … », « La médiane est … » (la paire « … et
 *     … » refusait l'ordre inverse dans 100 % des tirages) ; la règle et
 *     « demi-somme » sortent de la consigne (« Complète. »).
 *   - pair_qcm_unique REMPLACÉ, même clé, par un QCM sur la VALEUR (« Quelle
 *     est la médiane de cette série ? ») : médiane, moyenne (moyenne_pour_
 *     mediane), milieu du relevé non ordonné (milieu_sans_ranger), une seule des
 *     deux valeurs du milieu (centrale_voisine) ; quatre valeurs distinctes,
 *     multiples de 0,5, par construction. L'ancien QCM de méthode (règle nue,
 *     trois jeux de choix en tout) sort.
 *   - tableau_texte, tableau_texte_trou_saisie : effectif total de 11 à 30
 *     (jusqu'à 33 avant) ; fenêtre de valeurs glissante (les notes ne
 *     commencent plus toujours à 8, les âges à 9) ; pointure ajoutée ;
 *     effectifs de 1 à 8 ; nouveau tirage tant qu'une méthode fausse donne la
 *     médiane (50,0 % avant) : milieu_des_valeurs (la valeur du milieu de la
 *     ligne des valeurs, effectifs ignorés), rang_pour_valeur (le rang, « la
 *     13e », pris pour la valeur), mauvais_effectif (l'effectif de la colonne
 *     lu à la place de la valeur), et la valeur de plus grand effectif ;
 *     « Additionne les effectifs, puis détermine la médiane. » devient
 *     « Complète. ». Le « ans » des âges suit la réponse.
 *   - NOUVELLE carte « Médiane depuis un diagramme en bâtons » (batons_texte,
 *     saisie) : la série est donnée par un diagramme en bâtons (svgBatons de
 *     donnees.js : svgBarres, du module de 5e, pose le nom de l'axe vertical
 *     sur la graduation du haut) ; bâtons fins, axe des effectifs gradué de 1
 *     en 1, aucun effectif écrit ; 6 à 9 valeurs consécutives, effectif total de 11 à 30,
 *     pair ou impair, un effectif nul à l'intérieur dans une partie des tirages
 *     (comme le diagramme du cours) ; mêmes méthodes fausses exclues ; attribut
 *     alt qui décrit les axes et les effectifs.
 *   - Contrat des distracteurs exporté (REGLES_DISTRACTEURS_QCM), codes dans
 *     _meta.params.distracteurs.
 * Huit formats (onze avant), dont deux QCM.
 */
import {
    randomChoice, randInt,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { tableEnonce, ligneTable } from '../../5eme/statistiques/svgStats';
import {
    frNb, mediane, valeursCentralesPair, moyenne, developper, positionsMilieu, imgAvecAlt, svgBatons,
} from './donnees';

const ATTENDU_ID = 165;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Ancien libellé « Ranger, puis médiane (effectif impair) » : la carte
// « ranger » est sortie, le nom suit celui de l'effectif pair (coordinateur,
// 2026-09-23).
const L_IMPAIR = 'Médiane d\'un effectif impair';
const L_PAIR = 'Médiane d\'un effectif pair';
const L_TABLEAU = 'Médiane depuis un tableau d\'effectifs';
const L_BATONS = 'Médiane depuis un diagramme en bâtons';

// Rappel brouillon (décision 2026-09-23), IMPÉRATIF pour tout l'attendu :
// ordonner, cumuler des effectifs, (a + b) : 2 se font à la main. Forme maison
// du parc (gen_149, gen_162) ; juste avant la question, sans méthode soufflée.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

const ordinal = (k) => `${k}ᵉ`;

// ─── Contextes de série brute ────────────────────────────────
// Chaque contexte porte les quatre phrases du QCM d'interprétation (même
// moule : chacune contient la médiane m et son unité).
const CTX_SERIE = [
    {
        id: 'colis', min: 20, max: 60, unite: 'kg',
        intro: 'On a pesé des colis. Voici leurs masses, en kg.',
        cle: (m, sens) => `Au moins la moitié des colis pèsent ${m} kg ou ${sens}.`,
        milieu_sans_ranger: (m) => `La masse écrite au milieu du relevé est ${m} kg.`,
        moyenne_pour_mediane: (m) => `La moyenne des masses est ${m} kg.`,
        milieu_des_extremes: (m) => `${m} kg est à mi-chemin entre la plus petite et la plus grande masse.`,
    },
    {
        id: 'plants', min: 80, max: 130, unite: 'cm',
        intro: 'On a mesuré des jeunes plants. Voici leurs tailles, en cm.',
        cle: (m, sens) => `Au moins la moitié des plants mesurent ${m} cm ou ${sens}.`,
        milieu_sans_ranger: (m) => `La taille écrite au milieu du relevé est ${m} cm.`,
        moyenne_pour_mediane: (m) => `La moyenne des tailles est ${m} cm.`,
        milieu_des_extremes: (m) => `${m} cm est à mi-chemin entre la plus petite et la plus grande taille.`,
    },
    {
        id: 'temperatures', min: 8, max: 32, unite: '°C',
        intro: 'On a relevé la température à midi pendant plusieurs jours. Voici les relevés, en °C.',
        // « Au moins la moitié des jours, il a fait… » manquait d'une préposition
        // (coordinateur, 2026-09-23) : « un jour sur deux » dit la même chose.
        cle: (m, sens) => `Au moins un jour sur deux, il a fait ${m} °C ou ${sens} à midi.`,
        milieu_sans_ranger: (m) => `La température écrite au milieu du relevé est ${m} °C.`,
        moyenne_pour_mediane: (m) => `La moyenne des températures est ${m} °C.`,
        milieu_des_extremes: (m) => `${m} °C est à mi-chemin entre la plus basse et la plus haute température.`,
    },
    {
        id: 'points', min: 40, max: 95, unite: 'points',
        intro: 'Voici les nombres de points marqués par une équipe de basket lors de ses derniers matchs.',
        cle: (m, sens) => `Au moins un match sur deux, l'équipe a marqué ${m} points ou ${sens}.`,
        milieu_sans_ranger: (m) => `Le score écrit au milieu du relevé est de ${m} points.`,
        moyenne_pour_mediane: (m) => `La moyenne des scores est de ${m} points.`,
        milieu_des_extremes: (m) => `${m} points, c'est à mi-chemin entre le plus petit et le plus grand score.`,
    },
    {
        id: 'trajets', min: 12, max: 48, unite: 'min',
        intro: 'Voici les durées, en minutes, des trajets domicile-collège de quelques élèves.',
        cle: (m, sens) => `Au moins la moitié de ces élèves mettent ${m} min ou ${sens} pour venir.`,
        milieu_sans_ranger: (m) => `La durée écrite au milieu du relevé est ${m} min.`,
        moyenne_pour_mediane: (m) => `La durée moyenne est ${m} min.`,
        milieu_des_extremes: (m) => `${m} min est à mi-chemin entre la plus courte et la plus longue durée.`,
    },
];

/** La valeur « du milieu » du relevé NON ordonné (moyenne des deux si n pair). */
const milieuDuReleve = (vals) => {
    const pos = positionsMilieu(vals.length);
    return pos.reduce((s, r) => s + vals[r - 1], 0) / pos.length;
};

/**
 * n valeurs entières tirées AVEC remise (répétitions admises, comme dans le
 * cours), jusqu'à ce que la série vérifie `accepte` (boucle bornée) ; null
 * sinon.
 */
const tirerSerie = (ctx, n, accepte) => {
    for (let essai = 0; essai < 200; essai++) {
        const vals = Array.from({ length: n }, () => randInt(ctx.min, ctx.max));
        // Ne pas ordonner n'est jamais récompensé : le milieu du relevé brut ne
        // donne pas la médiane.
        if (milieuDuReleve(vals) === mediane(vals)) continue;
        if (accepte(vals)) return vals;
    }
    return null;
};

/**
 * Second chemin, par COMPTAGE (sans retrier) : au plus la moitié des valeurs
 * strictement sous m, au plus la moitié strictement au-dessus ; n impair : m
 * est une valeur de la série ; n pair : m est le milieu de la plus grande
 * valeur ≤ m et de la plus petite ≥ m. Throw sinon.
 */
const verifieMediane = (vals, m) => {
    const n = vals.length;
    let ok = 2 * vals.filter((v) => v < m).length <= n && 2 * vals.filter((v) => v > m).length <= n;
    if (n % 2) ok = ok && vals.includes(m);
    else ok = ok && Math.max(...vals.filter((v) => v <= m)) + Math.min(...vals.filter((v) => v >= m)) === 2 * m;
    if (!ok) throw new Error(`gen_165 : médiane ${m} de ${vals.join(' ; ')}`);
};

const releve = (ctx, vals) => `<p>${ctx.intro}</p><p>Relevé : ${vals.join(' ; ')}.</p>`;

// Replis déterministes vérifiés (colis), relevé non ordonné dont le milieu
// n'est pas la médiane :
//   7 valeurs, ordonnées 27 30 35 38 41 44 52 : médiane 38, milieu du relevé 52 ;
//   8 valeurs, ordonnées 22 29 31 34 37 40 45 51 : médiane 35,5, milieu du relevé (40 + 22) : 2 = 31.
const REPLI_IMPAIR = [41, 27, 35, 52, 30, 44, 38];
const REPLI_PAIR = [34, 29, 51, 40, 22, 45, 37, 31];

// ═════════════════════════════════════════════════════════════
// CARTE « Médiane d'un effectif impair »
// ═════════════════════════════════════════════════════════════

const genImpairSaisie = () => {
    const ctx = randomChoice(CTX_SERIE);
    const vals = tirerSerie(ctx, randomChoice([7, 9, 11]), () => true) || REPLI_IMPAIR;
    const m = mediane(vals);
    verifieMediane(vals, m);
    const payload = makeSaisie(String(m),
        releve(ctx, vals) + RAPPEL_BROUILLON + '<p><b>Quelle est la médiane de cette série ?</b></p>',
        { ...opts(L_IMPAIR), strict: false, suffixe: ` ${ctx.unite}` });
    payload._meta.params = { contexte: ctx.id, vals, mediane: m };
    return payload;
};

// Le QCM d'interprétation : chaque distracteur est une phrase FAUSSE sur m,
// nettement (moyenne et milieu des extrêmes à au moins 1 de m).
const accepteQcmImpair = (vals) => {
    const m = mediane(vals);
    return Math.abs(moyenne(vals) - m) >= 1
        && Math.abs((Math.min(...vals) + Math.max(...vals)) / 2 - m) >= 1;
};

const genImpairQcm = () => {
    let ctx = randomChoice(CTX_SERIE);
    let vals = tirerSerie(ctx, randomChoice([7, 9, 11]), accepteQcmImpair);
    if (!vals) {
        // Repli déterministe vérifié : colis 41 27 35 52 30 44 50 ; ordonnée 27 30
        // 35 41 44 50 52, médiane 41 ; milieu du relevé 52 ; moyenne 39,86 ;
        // milieu des extrêmes 39,5.
        ctx = CTX_SERIE[0];
        vals = [41, 27, 35, 52, 30, 44, 50];
    }
    const m = mediane(vals);
    const sens = randomChoice(['plus', 'moins']);
    // Second chemin : la clé est vraie, chaque distracteur est faux.
    verifieMediane(vals, m);
    const auMoins = sens === 'plus' ? vals.filter((v) => v >= m).length : vals.filter((v) => v <= m).length;
    if (2 * auMoins < vals.length || milieuDuReleve(vals) === m || !accepteQcmImpair(vals)) {
        throw new Error(`gen_165 : QCM impair ${vals}`);
    }
    const familles = ['milieu_sans_ranger', 'moyenne_pour_mediane', 'milieu_des_extremes'];
    const bonne = ctx.cle(m, sens);
    const payload = makeQcm([bonne, ...familles.map((f) => ctx[f](m))], bonne,
        releve(ctx, vals)
        + `<p>La médiane de cette série est ${m} ${ctx.unite}.</p>`
        + RAPPEL_BROUILLON
        + '<p><b>Quelle phrase est vraie ?</b></p>',
        opts(L_IMPAIR));
    payload._meta.params = { contexte: ctx.id, vals, mediane: m, sens, distracteurs: familles };
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Médiane d'un effectif pair »
// ═════════════════════════════════════════════════════════════

const genPairSaisie = () => {
    const ctx = randomChoice(CTX_SERIE);
    const vals = tirerSerie(ctx, randomChoice([6, 8, 10, 12]), () => true) || REPLI_PAIR;
    const m = mediane(vals);
    verifieMediane(vals, m);
    const payload = makeSaisie(frNb(m),
        releve(ctx, vals) + RAPPEL_BROUILLON + '<p><b>Quelle est la médiane de cette série ?</b></p>',
        { ...opts(L_PAIR), strict: false, suffixe: ` ${ctx.unite}` });
    payload._meta.params = { contexte: ctx.id, vals, mediane: m };
    return payload;
};

// Trous ancrés par leur rang : ni la n/2-ième ni la (n/2 + 1)-ième valeur du
// relevé NON ordonné ne vaut la bonne, sinon ne pas ordonner réussirait ce trou.
const accepteTrousPair = (vals) => {
    const n = vals.length;
    const [a, b] = valeursCentralesPair(vals);
    return vals[n / 2 - 1] !== a && vals[n / 2] !== b;
};

const genPairTrous = () => {
    const ctx = randomChoice(CTX_SERIE);
    const vals = tirerSerie(ctx, randomChoice([6, 8, 10, 12]), accepteTrousPair) || REPLI_PAIR;
    const n = vals.length;
    const [a, b] = valeursCentralesPair(vals);
    const m = mediane(vals);
    verifieMediane(vals, m);
    if (a + b !== 2 * m) throw new Error('gen_165 : trous pairs');
    // Trois trous ANCRÉS, un par ligne : aucune paire « … et … » à ordre libre.
    const phrase = [
        `La ${ordinal(n / 2)} valeur est {{${a}}} ${ctx.unite}.`,
        `La ${ordinal(n / 2 + 1)} valeur est {{${b}}} ${ctx.unite}.`,
        `La médiane est {{${frNb(m)}}} ${ctx.unite}.`,
    ].join('\n');
    const payload = makeTexteTrou(phrase,
        releve(ctx, vals) + RAPPEL_BROUILLON + '<p><b>Complète.</b></p>',
        { ...opts(L_PAIR), mode: 'saisie', strict: false });
    payload._meta.params = { contexte: ctx.id, vals, a, b, mediane: m };
    return payload;
};

// QCM sur la VALEUR : médiane, moyenne, milieu du relevé non ordonné, une des
// deux valeurs du milieu ; quatre valeurs distinctes, multiples de 0,5.
const optionsPair = (vals) => {
    const m = mediane(vals);
    const [a, b] = valeursCentralesPair(vals);
    if (a === b) return null;                              // centrale_voisine vaudrait la clé
    const moy = moyenne(vals);
    if (!Number.isInteger(2 * moy)) return null;           // moyenne « propre » (multiple de 0,5)
    const options = [
        { code: null, v: m },
        { code: 'moyenne_pour_mediane', v: moy },
        { code: 'milieu_sans_ranger', v: milieuDuReleve(vals) },
        { code: 'centrale_voisine', v: randomChoice([a, b]) },
    ];
    if (new Set(options.map((o) => o.v)).size !== 4) return null;
    // Une seule des deux valeurs du milieu parmi les choix : si la moyenne ou le
    // milieu du relevé tombait sur l'autre, la clé serait encadrée par les deux.
    return options.filter((o) => o.v === a || o.v === b).length === 1 ? options : null;
};

const genPairQcm = () => {
    let ctx = CTX_SERIE[0];
    let vals = null;
    let options = null;
    for (let essai = 0; essai < 60 && !options; essai++) {
        ctx = randomChoice(CTX_SERIE);
        vals = tirerSerie(ctx, randomChoice([6, 8, 10]), (v) => optionsPair(v) !== null);
        if (vals) options = optionsPair(vals);
    }
    if (!options) {
        // Repli déterministe vérifié : colis 34 29 51 40 22 45 37 30 ; ordonnée 22
        // 29 30 34 37 40 45 51 ; médiane 35,5 ; moyenne 36 ; milieu du relevé
        // (40 + 22) : 2 = 31 ; valeur du milieu 34.
        ctx = CTX_SERIE[0];
        vals = [34, 29, 51, 40, 22, 45, 37, 30];
        options = [
            { code: null, v: 35.5 }, { code: 'moyenne_pour_mediane', v: 36 },
            { code: 'milieu_sans_ranger', v: 31 }, { code: 'centrale_voisine', v: 34 },
        ];
    }
    verifieMediane(vals, options[0].v);
    const affiche = (v) => `${frNb(v)} ${ctx.unite}`;
    const choix = options.map((o) => affiche(o.v));
    const payload = makeQcm(choix, choix[0],
        releve(ctx, vals) + RAPPEL_BROUILLON + '<p><b>Quelle est la médiane de cette série ?</b></p>',
        opts(L_PAIR));
    payload._meta.params = {
        contexte: ctx.id, vals, mediane: options[0].v,
        valeurs: Object.fromEntries(options.slice(1).map((o) => [o.code, o.v])),
        distracteurs: options.slice(1).map((o) => o.code),
    };
    return payload;
};

// ═════════════════════════════════════════════════════════════
// Séries données par un tableau ou un diagramme en bâtons
// ═════════════════════════════════════════════════════════════

/**
 * Les valeurs que donnent les méthodes fausses nommées sur une série
 * valeur → effectif (valeurs croissantes) :
 *   milieu_des_valeurs : la valeur du milieu de la ligne des valeurs, sans les effectifs ;
 *   rang_pour_valeur   : le rang de la médiane (13 pour « la 13e valeur ») ;
 *   mauvais_effectif   : l'effectif de la colonne de la médiane lu comme une valeur ;
 *   plus_grand_effectif : la valeur de plus grand effectif (s'il est unique).
 */
const methodesFausses = (valeurs, effectifs) => {
    const serie = developper(valeurs, effectifs);
    const n = serie.length;
    const rangs = positionsMilieu(n);
    const out = {
        milieu_des_valeurs: [mediane(valeurs)],
        rang_pour_valeur: n % 2 ? rangs : [...rangs, (rangs[0] + rangs[1]) / 2],
        mauvais_effectif: rangs.map((r) => effectifs[valeurs.indexOf(serie[r - 1])]),
        plus_grand_effectif: [],
    };
    const maxE = Math.max(...effectifs);
    if (effectifs.filter((e) => e === maxE).length === 1) out.plus_grand_effectif.push(valeurs[effectifs.indexOf(maxE)]);
    return out;
};

/** Vrai si aucune méthode fausse ne donne la médiane. */
const aucuneMethodeFausse = (valeurs, effectifs) => {
    const m = mediane(developper(valeurs, effectifs));
    return Object.values(methodesFausses(valeurs, effectifs)).every((l) => !l.includes(m));
};

// ═════════════════════════════════════════════════════════════
// CARTE « Médiane depuis un tableau d'effectifs »
// ═════════════════════════════════════════════════════════════

const CTX_TABLE = [
    { id: 'fratrie', nom: 'le nombre de frères et sœurs des élèves d\'une classe', valeurLabel: 'Frères et sœurs', plage: [0, 5], unite: '' },
    { id: 'notes', nom: 'les notes obtenues à un contrôle par les élèves d\'une classe', valeurLabel: 'Note', plage: [5, 18], unite: '' },
    { id: 'ages', nom: 'l\'âge des enfants d\'un centre de loisirs', valeurLabel: 'Âge en ans', plage: [7, 14], unite: 'ans' },
    { id: 'buts', nom: 'le nombre de buts marqués par une équipe de football à chaque match', valeurLabel: 'Buts', plage: [0, 6], unite: '' },
    { id: 'pointures', nom: 'la pointure des élèves d\'une classe', valeurLabel: 'Pointure', plage: [35, 42], unite: '' },
];

/** k valeurs consécutives dans la plage (fenêtre glissante), effectifs de 1 à 8, total 11 à 30. */
const tirerTable = () => {
    for (let essai = 0; essai < 300; essai++) {
        const ctx = randomChoice(CTX_TABLE);
        const largeur = ctx.plage[1] - ctx.plage[0] + 1;
        const k = randInt(4, Math.min(6, largeur));
        const debut = randInt(ctx.plage[0], ctx.plage[1] - k + 1);
        const valeurs = Array.from({ length: k }, (_, i) => debut + i);
        const effectifs = valeurs.map(() => randInt(1, 8));
        const n = effectifs.reduce((a, b) => a + b, 0);
        if (n < 11 || n > 30) continue;
        if (!aucuneMethodeFausse(valeurs, effectifs)) continue;
        return { ctx, valeurs, effectifs, serie: developper(valeurs, effectifs) };
    }
    // Repli déterministe vérifié : notes 10 à 15, effectifs 2 6 5 1 3 4 (total
    // 21, médiane = 11e valeur = 12) ; milieu des valeurs 12,5, rang 11,
    // effectif de la colonne 5, plus grand effectif pour 11 : aucune ne vaut 12.
    const ctx = CTX_TABLE[1];
    const valeurs = [10, 11, 12, 13, 14, 15];
    const effectifs = [2, 6, 5, 1, 3, 4];
    if (!aucuneMethodeFausse(valeurs, effectifs)) throw new Error('gen_165 : repli tableau');
    return { ctx, valeurs, effectifs, serie: developper(valeurs, effectifs) };
};

const tableEffectifs = (d) => tableEnonce(
    ligneTable(d.ctx.valeurLabel, d.valeurs),
    ligneTable('Effectif', d.effectifs));

const avecUnite = (u) => (u ? ` ${u}` : '');

const genTableSaisie = () => {
    const d = tirerTable();
    const m = mediane(d.serie);
    verifieMediane(d.serie, m);
    const payload = makeSaisie(frNb(m),
        `<p>Le tableau donne ${d.ctx.nom}.</p>${tableEffectifs(d)}`
        + RAPPEL_BROUILLON
        + '<p><b>Quelle est la médiane de cette série ?</b></p>',
        { ...opts(L_TABLEAU), strict: false, suffixe: avecUnite(d.ctx.unite) });
    payload._meta.params = { contexte: d.ctx.id, valeurs: d.valeurs, effectifs: d.effectifs, n: d.serie.length, mediane: m };
    return payload;
};

const genTableTrous = () => {
    const d = tirerTable();
    const n = d.serie.length;
    const m = mediane(d.serie);
    verifieMediane(d.serie, m);
    if (n !== d.effectifs.reduce((a, b) => a + b, 0)) throw new Error('gen_165 : effectif total');
    const phrase = [
        `L'effectif total est {{${n}}}.`,
        `La médiane est {{${frNb(m)}}}${avecUnite(d.ctx.unite)}.`,
    ].join('\n');
    const payload = makeTexteTrou(phrase,
        `<p>Le tableau donne ${d.ctx.nom}.</p>${tableEffectifs(d)}`
        + RAPPEL_BROUILLON
        + '<p><b>Complète.</b></p>',
        { ...opts(L_TABLEAU), mode: 'saisie', strict: false });
    payload._meta.params = { contexte: d.ctx.id, valeurs: d.valeurs, effectifs: d.effectifs, n, mediane: m };
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Médiane depuis un diagramme en bâtons »
// ═════════════════════════════════════════════════════════════

const CTX_BATONS = [
    { id: 'notes', nom: 'les notes obtenues à un contrôle par les élèves d\'une classe de 4e', axe: 'Notes', plage: [5, 18], question: 'Quelle est la note médiane ?', unite: '' },
    { id: 'livres', nom: 'le nombre de livres lus pendant les vacances par les élèves d\'une classe', axe: 'Livres lus', plage: [0, 9], question: 'Quelle est la médiane de cette série ?', unite: '' },
    { id: 'pointures', nom: 'la pointure des élèves d\'une classe', axe: 'Pointures', plage: [34, 43], question: 'Quelle est la pointure médiane ?', unite: '' },
    { id: 'buts', nom: 'le nombre de buts marqués par une équipe de handball à chaque match d\'un tournoi', axe: 'Buts', plage: [10, 22], question: 'Quelle est la médiane de cette série ?', unite: '' },
];

/**
 * 6 à 9 valeurs consécutives ; effectifs de 0 à 6, le premier et le dernier au
 * moins 1 ; un effectif nul à l'intérieur dans une partie des tirages ; total
 * de 11 à 30 ; aucune méthode fausse ne donne la médiane.
 */
const tirerBatons = () => {
    for (let essai = 0; essai < 400; essai++) {
        const ctx = randomChoice(CTX_BATONS);
        const largeur = ctx.plage[1] - ctx.plage[0] + 1;
        const k = randInt(6, Math.min(9, largeur));
        const debut = randInt(ctx.plage[0], ctx.plage[1] - k + 1);
        const valeurs = Array.from({ length: k }, (_, i) => debut + i);
        const effectifs = valeurs.map(() => randInt(1, 6));
        if (randInt(0, 1) === 0) effectifs[randInt(1, k - 2)] = 0;   // un effectif nul à l'intérieur
        const n = effectifs.reduce((a, b) => a + b, 0);
        if (n < 11 || n > 30) continue;
        if (!aucuneMethodeFausse(valeurs, effectifs)) continue;
        return { ctx, valeurs, effectifs, serie: developper(valeurs, effectifs) };
    }
    // Repli déterministe vérifié : notes 8 à 14, effectifs 2 5 1 0 3 6 4 (total
    // 21, un effectif nul pour 11, médiane = 11e valeur = 12) ; milieu des
    // valeurs 11, rang 11, effectif de la colonne 3, plus grand effectif pour 13 :
    // aucune ne vaut 12.
    const ctx = CTX_BATONS[0];
    const valeurs = [8, 9, 10, 11, 12, 13, 14];
    const effectifs = [2, 5, 1, 0, 3, 6, 4];
    if (!aucuneMethodeFausse(valeurs, effectifs)) throw new Error('gen_165 : repli bâtons');
    return { ctx, valeurs, effectifs, serie: developper(valeurs, effectifs) };
};

const figureBatons = (d) => {
    const svg = svgBatons(d.valeurs, d.effectifs, { axeX: d.ctx.axe });
    const alt = `Diagramme en bâtons : en abscisse, ${d.ctx.axe.toLowerCase()} de ${d.valeurs[0]} à ${d.valeurs[d.valeurs.length - 1]} ; `
        + 'en ordonnée, les effectifs, gradués de 1 en 1 ; '
        + `effectif de chaque valeur : ${d.valeurs.map((v, i) => `${v} : ${d.effectifs[i]}`).join(' ; ')}.`;
    return imgAvecAlt(svg, alt, 'medium');
};

const genBatonsSaisie = () => {
    const d = tirerBatons();
    const m = mediane(d.serie);
    const n = d.serie.length;
    verifieMediane(d.serie, m);
    const payload = makeSaisie(frNb(m),
        `<p>Le diagramme en bâtons donne ${d.ctx.nom}.</p>`
        + RAPPEL_BROUILLON
        + `<p><b>${d.ctx.question}</b></p>`,
        { ...opts(L_BATONS), strict: false, suffixe: avecUnite(d.ctx.unite) });
    payload.elements_additionnels = figureBatons(d);
    payload._meta.params = { contexte: d.ctx.id, valeurs: d.valeurs, effectifs: d.effectifs, n, mediane: m };
    return payload;
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    impair_qcm_unique: {
        support: 'relevé brut de 7, 9 ou 11 entiers, répétitions admises, dans la plage du contexte ; la médiane m est donnée ; quatre phrases qui contiennent toutes m',
        cle: 'les mots du cours (« au moins 13 élèves … supérieure ou égale à 60 minutes ») : « Au moins la moitié des colis pèsent m kg ou plus » (ou « ou moins »), vérifiée sur la série',
        familles: {
            milieu_sans_ranger: 'la valeur écrite au milieu du relevé NON ordonné prise pour la médiane (DEPP 2de 2025 q.14, 9,2 %, « omet de ranger la série ») ; fausse : le milieu du relevé n\'est jamais m (nouveau tirage)',
            moyenne_pour_mediane: 'la médiane prise pour la moyenne (q.14, 6,4 %, « confond moyenne et médiane ») ; fausse : la moyenne est à au moins 1 de m',
            milieu_des_extremes: 'la médiane prise pour le milieu entre la plus petite et la plus grande valeur (q.14, « moitié de 20 », 1,1 %) ; fausse : ce milieu est à au moins 1 de m',
        },
        supprimees: {
            mode: '« la valeur qui revient le plus souvent » : absurde sur une série sans répétition (100 % des tirages avant)',
            etendue: '« l\'écart entre la plus grande et la plus petite valeur » : étendue, absente de l\'attendu et du cours',
        },
    },
    pair_qcm_unique: {
        support: 'relevé brut de 6, 8 ou 10 entiers, répétitions admises, les deux valeurs du milieu différentes, moyenne multiple de 0,5 ; « Quelle est la médiane de cette série ? »',
        cle: '(a + b) : 2 des deux valeurs du milieu de la série ordonnée',
        familles: {
            moyenne_pour_mediane: 'la moyenne de la série (q.14, 6,4 %)',
            milieu_sans_ranger: '(a + b) : 2 des deux valeurs du milieu du relevé NON ordonné (q.14, 9,2 %)',
            centrale_voisine: 'une seule des deux valeurs du milieu, tirée au hasard (jamais les deux : la clé serait toujours encadrée)',
        },
        garantie: 'quatre valeurs deux à deux distinctes, vérifiées (nouveau tirage sinon, borné, puis repli vérifié) ; toutes affichées par le même helper',
        supprimee: 'l\'ancien QCM de méthode (« Comment obtient-on la médiane ? », trois jeux de choix en tout, clé la plus longue) : règle nue, aucun calcul',
    },
    tableau_et_batons: 'saisies sans distracteur ; nouveau tirage tant qu\'une méthode fausse nommée donne la médiane : milieu_des_valeurs (valeur du milieu de la ligne des valeurs), rang_pour_valeur (13 pour « la 13e valeur »), mauvais_effectif (l\'effectif de la colonne de la médiane lu comme une valeur), plus_grand_effectif (la valeur la plus fréquente)',
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    impair_texte: genImpairSaisie,
    impair_qcm_unique: genImpairQcm,
    pair_texte: genPairSaisie,
    pair_texte_trou_saisie: genPairTrous,
    pair_qcm_unique: genPairQcm,
    tableau_texte: genTableSaisie,
    tableau_texte_trou_saisie: genTableTrous,
    batons_texte: genBatonsSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'impair', label: L_IMPAIR, formatsList: buildFormats('impair') },
    { id: 'pair', label: L_PAIR, formatsList: buildFormats('pair') },
    { id: 'tableau', label: L_TABLEAU, formatsList: buildFormats('tableau') },
    { id: 'batons', label: L_BATONS, formatsList: buildFormats('batons') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
